/**
 * Causal Conversational FinOps Bot (Patent Sub-system X)
 */
import React, { useState } from 'react';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Loader2, FlaskConical, MessageSquare, Sparkles, ArrowRight } from 'lucide-react';
import DemoDataBanner from '../components/DemoDataBanner';
import ProvenByTests from '../components/ProvenByTests';
import BrandTitle from '../components/BrandTitle';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SAMPLE_QUERIES = [
  "Why did the bill jump $120K this month?",
  "Which feature is losing money on our biggest customers?",
  "Which K8s pod is costing me the most?",
  "Is our cost growth healthy or wasteful?",
  "What's the carbon footprint of my AI workload?",
];

const CausalBot = () => {
  const [question, setQuestion] = useState(SAMPLE_QUERIES[0]);
  const [answer, setAnswer] = useState(null);
  const [loading, setLoading] = useState(false);

  const ask = async (q) => {
    setQuestion(q);
    setLoading(true);
    setAnswer(null);
    try {
      const r = await axios.post(`${API_URL}/causal-bot/ask`, { question: q });
      setAnswer(r.data);
    } catch (e) {
      setAnswer({ one_line_answer: 'Sorry, I could not answer that.', causal_chain: [], recommended_action: '', matched_intent: 'error', intent_label: 'Error', question: q });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100" data-testid="causal-bot-page">
      <Helmet><title>Causal FinOps Bot | CARTIEAI</title></Helmet>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
        <DemoDataBanner />
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-widest text-emerald-300 border border-emerald-500/30 rounded-md px-2 py-1 mb-5" data-testid="cb-patent-badge">
            <FlaskConical className="w-3 h-3" /> Proprietary engine
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight leading-[1.05]"><BrandTitle>Causal FinOps Bot</BrandTitle></h1>
          <div className="mt-3"><ProvenByTests engine="causal-bot" /></div>
          <p className="mt-4 text-base sm:text-lg text-slate-300 leading-relaxed">
            Natural-language "why did the bill spike?" — answered by chaining{" "}
            <span className="text-emerald-300">Cost-Bisect → Cost Causality → K8s Shapley → Per-Feature P&amp;L</span> automatically.
          </p>
          <p className="mt-3 text-sm text-slate-500">
            CFO Copilots from Pigment / Mosaic answer revenue-side questions. This is the only one that answers cloud-cost causality questions by orchestrating the underlying proprietary subsystems.
          </p>
        </div>

        <Card className="mt-10 bg-slate-900/60 border border-white/10 shadow-sm border-emerald-500/30">
          <CardHeader>
            <CardTitle className="text-white text-lg flex items-center gap-2"><MessageSquare className="w-5 h-5 text-emerald-300" /> Ask the bot</CardTitle>
            <CardDescription>Pick a query or paste your own — the bot classifies intent and orchestrates the right causality stack.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex flex-wrap gap-2" data-testid="cb-samples">
              {SAMPLE_QUERIES.map((q, i) => (
                <button
                  key={i}
                  onClick={() => ask(q)}
                  data-testid={`cb-sample-${i}`}
                  className="text-xs px-3 py-1.5 rounded-full border border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/10 transition-colors"
                >
                  {q}
                </button>
              ))}
            </div>
            <div className="flex gap-2 mt-4">
              <input
                type="text"
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                className="flex-1 bg-slate-950 border border-white/10 rounded-md px-3 py-2 text-sm text-white focus:outline-none focus:border-emerald-500/60"
                placeholder="Ask anything about your cloud cost..."
                data-testid="cb-input"
              />
              <Button onClick={() => ask(question)} disabled={loading || question.length < 3} className="bg-emerald-600 hover:bg-emerald-500" data-testid="cb-ask-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Sparkles className="w-4 h-4 mr-1" /> Ask</>}
              </Button>
            </div>
          </CardContent>
        </Card>

        {answer && (
          <Card className="mt-6 bg-gradient-to-br from-emerald-500/10 to-emerald-500/5 border-emerald-500/30" data-testid="cb-answer">
            <CardContent className="p-6">
              <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-mono mb-2" data-testid="cb-intent">
                Intent: {answer.intent_label}
              </div>
              <div className="text-lg sm:text-xl text-white leading-relaxed" data-testid="cb-one-line">{answer.one_line_answer}</div>

              {answer.causal_chain && answer.causal_chain.length > 0 && (
                <div className="mt-6 space-y-2" data-testid="cb-chain">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">Causality chain</div>
                  {answer.causal_chain.map((step, i) => (
                    <div key={i} className="flex items-start gap-3 p-3 rounded border border-white/10 bg-slate-950/60">
                      <ArrowRight className="w-4 h-4 text-emerald-300 mt-1 shrink-0" />
                      <div className="flex-1">
                        <div className="text-xs text-emerald-300 font-mono font-semibold">{step.sub_system}</div>
                        <div className="text-sm text-slate-200 mt-1">{step.finding}</div>
                        {step.dollar_impact_usd !== null && step.dollar_impact_usd !== undefined && (
                          <div className="text-xs text-emerald-300 font-mono mt-1">${step.dollar_impact_usd.toLocaleString('en-US', { maximumFractionDigits: 0 })}</div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {answer.recommended_action && (
                <div className="mt-6 p-4 rounded border border-emerald-500/30 bg-emerald-500/5" data-testid="cb-action">
                  <div className="text-[10px] uppercase tracking-widest text-emerald-300 mb-1">Recommended action</div>
                  <div className="text-sm text-slate-200">{answer.recommended_action}</div>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default CausalBot;
